/**
 * CDM Frontier-AI Risk Overlay — Track 3.12.
 *
 * Bank's exposure overlay onto frontier-AI events: when a capability
 * moment / jailbreak / safety incident / model release drops, this page
 * shows our affected assets immediately.
 *
 * Data source: /api/cdm/frontier-overlay (rollup) + drill-downs into
 * /api/cdm/frontier-overlay/vendor/{vendor} and .../event/{id} and
 * .../incidents.
 *
 * Distinct from /app/frontier-index — that's a frontier-AI catalogue;
 * this page is the bank's exposure lens.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Brain, Bot, AlertTriangle, RefreshCw, ChevronLeft,
  Sparkles, Shield, ExternalLink, Filter,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Response types ───────────────────────────────────────────────────────────

interface FrontierEvent {
  id:           string
  kind:         'capability_moment' | 'jailbreak' | 'safety_incident' | 'model_release'
  occurred_at:  string
  model_vendor: string | null
  model_name:   string | null
  severity:     string | null
  summary:      string
  source_url:   string | null
}

interface VendorAsset {
  asset_id:        string
  hostname:        string | null
  archetype:       string | null
  last_seen:       string | null
  evidence_source: 'tag' | 'inferred_from_software' | 'both'
}

interface HotFinding {
  event_id:    string
  vendor:      string
  asset_count: number
  kind:        string
  severity:    string | null
  summary:     string
  occurred_at: string
}

interface OverlaySummary {
  recent_events:   FrontierEvent[]
  vendor_exposure: Record<string, VendorAsset[]>
  hot_findings:    HotFinding[]
  untagged_count:  number
  lookback_days:   number
  generated_at:    string
}

interface IncidentRow {
  incident_id:    number | string | null
  title:          string
  date:           string | null
  developers:     string[]
  harm_types:     string[]
  url:            string | null
  vendor:         string | null
  exposure_count: number
}

// ── Display helpers ──────────────────────────────────────────────────────────

const KIND_TONE: Record<string, string> = {
  capability_moment: 'bg-purple/10 text-purple-light border-purple/30',
  jailbreak:         'bg-rose/10 text-rose-light border-rose/30',
  safety_incident:   'bg-amber/10 text-amber-light border-amber/30',
  model_release:     'bg-blue/10 text-blue-light border-blue/30',
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'bg-rose/10 text-rose-light border-rose/30',
  high:     'bg-rose/10 text-rose-light border-rose/30',
  medium:   'bg-amber/10 text-amber-light border-amber/30',
  low:      'bg-cyan/10 text-cyan border-cyan/30',
  info:     'bg-surface-raised text-text-muted border-border',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toISOString().slice(0, 10) } catch { return iso }
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function CdmFrontierOverlayPage() {
  const [data, setData] = useState<OverlaySummary | null>(null)
  const [incidents, setIncidents] = useState<IncidentRow[]>([])
  const [loading, setLoading] = useState(false)
  const [vendorFilter, setVendorFilter] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/cdm/frontier-overlay').then(r => r.data),
      api.get('/api/cdm/frontier-overlay/incidents', { params: { limit: 50 } }).then(r => r.data),
    ])
      .then(([summary, incs]) => {
        setData(summary as OverlaySummary)
        setIncidents((incs?.incidents || []) as IncidentRow[])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const vendorList = useMemo(() => {
    if (!data) return []
    return Object.entries(data.vendor_exposure)
      .map(([v, assets]) => ({ vendor: v, assets }))
      .sort((a, b) => b.assets.length - a.assets.length)
  }, [data])

  const totalAssets = useMemo(
    () => vendorList.reduce((acc, v) => acc + v.assets.length, 0),
    [vendorList])

  const hotEvents24h = useMemo(() => {
    if (!data) return 0
    const dayAgo = Date.now() - 24 * 3600 * 1000
    return data.hot_findings.filter(f => {
      try { return new Date(f.occurred_at).getTime() >= dayAgo } catch { return false }
    }).length
  }, [data])

  const filteredEvents = useMemo(() => {
    if (!data) return []
    if (!vendorFilter) return data.recent_events
    return data.recent_events.filter(e => e.model_vendor === vendorFilter)
  }, [data, vendorFilter])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Brain size={18} className="text-purple-light" /> FRONTIER-AI RISK OVERLAY
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          capability moments × jailbreaks × safety incidents × our exposure
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/app/frontier-index" className="btn-ghost text-[11px]" title="Frontier-AI catalogue (separate page)">
            <Sparkles size={11} /> Frontier index
          </Link>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Hero tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Bot}            label="Vendors in our stack"   value={vendorList.length} tone="text-purple-light" />
        <Tile icon={AlertTriangle}  label="Hot events (24h)"       value={hotEvents24h}      tone="text-rose-light"   warn={hotEvents24h > 0} />
        <Tile icon={Shield}         label="AI safety incidents"    value={incidents.length}  tone="text-amber-light" />
        <Tile icon={Brain}          label="Untagged AI assets"     value={data?.untagged_count ?? 0} tone="text-cyan" warn={(data?.untagged_count ?? 0) > 0} />
      </div>

      {vendorFilter && (
        <div className="flex items-center gap-2 text-xs">
          <Filter size={12} className="text-text-muted" />
          <span className="text-text-secondary">
            Filtered by vendor: <strong className="text-text-primary">{vendorFilter}</strong>
          </span>
          <button type="button" onClick={() => setVendorFilter(null)}
                  className="text-blue-light hover:underline">clear</button>
        </div>
      )}

      {/* Top section: recent frontier events */}
      <div className="card p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border bg-surface-raised flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Sparkles size={14} className="text-purple-light" />
            Recent frontier events (last {data?.lookback_days ?? 30}d)
          </h2>
          <span className="text-[10px] text-text-muted">
            {filteredEvents.length} events
          </span>
        </div>
        <div className="divide-y divide-border">
          {filteredEvents.length === 0 && (
            <div className="px-3 py-6 text-center text-text-muted text-sm">
              {loading ? 'Loading…' : 'No frontier events in window.'}
            </div>
          )}
          {filteredEvents.slice(0, 25).map(e => {
            const exposure = e.model_vendor && data?.vendor_exposure[e.model_vendor]?.length || 0
            return (
              <div key={e.id} className="px-3 py-2 hover:bg-surface-raised/40 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={clsx('badge text-[9px] border', KIND_TONE[e.kind] || 'bg-surface-raised text-text-muted border-border')}>
                    {e.kind.replace('_', ' ')}
                  </span>
                  <span className={clsx('badge text-[9px] border', SEVERITY_TONE[(e.severity || 'medium').toLowerCase()] || SEVERITY_TONE.medium)}>
                    {e.severity || 'medium'}
                  </span>
                  {e.model_vendor && (
                    <span className="text-[10px] uppercase tracking-widest text-blue-light cursor-pointer hover:underline"
                          onClick={() => setVendorFilter(e.model_vendor)}>
                      {e.model_vendor}
                    </span>
                  )}
                  {e.model_name && (
                    <span className="text-[11px] font-mono text-text-secondary">{e.model_name}</span>
                  )}
                  <span className="ml-auto text-[10px] text-text-muted font-mono">
                    {formatDate(e.occurred_at)}
                  </span>
                </div>
                <div className="mt-1 text-text-secondary">
                  {e.summary || '—'}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px]">
                  {exposure > 0 ? (
                    <span className="badge text-[9px] border bg-rose/10 text-rose-light border-rose/30 inline-flex items-center gap-1">
                      <AlertTriangle size={9} /> Affected: {exposure} of our assets
                    </span>
                  ) : (
                    <span className="text-text-muted">No exposure</span>
                  )}
                  {e.source_url && (
                    <a href={e.source_url} target="_blank" rel="noopener noreferrer"
                       className="text-blue-light hover:underline inline-flex items-center gap-1">
                      <ExternalLink size={10} /> source
                    </a>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Middle section: vendor exposure matrix */}
      <div className="card p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border bg-surface-raised">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Bot size={14} className="text-blue-light" />
            Vendor exposure matrix
          </h2>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-surface-card">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-right px-3 py-2">Assets</th>
              <th className="text-left px-3 py-2">Sample asset</th>
              <th className="text-left px-3 py-2">Last seen</th>
              <th className="text-left px-3 py-2">Evidence</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {vendorList.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                No vendor exposure detected yet — tag assets with <code className="text-blue-light">model_vendor=…</code>{' '}
                or install our Python-package-name heuristic adapters to populate this view.
              </td></tr>
            )}
            {vendorList.map(({ vendor, assets }) => {
              const sample = assets[0]
              return (
                <tr key={vendor} className={clsx('hover:bg-surface-raised/40 cursor-pointer',
                                                  vendorFilter === vendor && 'bg-blue/5')}
                    onClick={() => setVendorFilter(v => v === vendor ? null : vendor)}>
                  <td className="px-3 py-1.5">
                    <span className="badge text-[9px] border bg-purple/10 text-purple-light border-purple/30 uppercase">
                      {vendor}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold text-text-primary">
                    {assets.length}
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary font-mono text-[11px]">
                    {sample?.hostname || sample?.asset_id?.slice(0, 8) + '…' || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-text-muted font-mono text-[11px]">
                    {formatDate(sample?.last_seen || null)}
                  </td>
                  <td className="px-3 py-1.5">
                    {sample?.evidence_source && (
                      <span className={clsx('badge text-[9px] border',
                        sample.evidence_source === 'tag'                       ? 'bg-cyan/10 text-cyan border-cyan/30' :
                        sample.evidence_source === 'inferred_from_software'    ? 'bg-amber/10 text-amber-light border-amber/30' :
                                                                                  'bg-purple/10 text-purple-light border-purple/30')}>
                        {sample.evidence_source.replace(/_/g, ' ')}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          {totalAssets > 0 && (
            <tfoot className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted">
                <td className="px-3 py-1.5 font-semibold">Total</td>
                <td className="px-3 py-1.5 text-right font-mono font-bold text-text-primary">
                  {totalAssets}
                </td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Bottom section: AI safety incidents with exposure count */}
      <div className="card p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border bg-surface-raised flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-light" />
            AI safety incidents (AIID feed)
          </h2>
          <span className="text-[10px] text-text-muted">
            {incidents.length} incidents
          </span>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-surface-card">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Harm types</th>
              <th className="text-right px-3 py-2">Our exposure</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {incidents.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                AIID cache empty — visit /app/ai-incidents to populate.
              </td></tr>
            )}
            {incidents.map(inc => (
              <tr key={String(inc.incident_id || inc.title)} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5 text-text-muted font-mono text-[11px]">
                  {inc.date || '—'}
                </td>
                <td className="px-3 py-1.5 text-text-secondary">
                  {inc.url ? (
                    <a href={inc.url} target="_blank" rel="noopener noreferrer"
                       className="text-blue-light hover:underline inline-flex items-center gap-1">
                      {inc.title} <ExternalLink size={9} />
                    </a>
                  ) : inc.title}
                </td>
                <td className="px-3 py-1.5">
                  {inc.vendor ? (
                    <span className="badge text-[9px] border bg-purple/10 text-purple-light border-purple/30 uppercase">
                      {inc.vendor}
                    </span>
                  ) : (
                    <span className="text-text-muted text-[10px]">unknown</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-text-muted text-[10px]">
                  {inc.harm_types.slice(0, 3).join(', ') || '—'}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {inc.exposure_count > 0 ? (
                    <span className="badge text-[9px] border bg-rose/10 text-rose-light border-rose/30 inline-flex items-center gap-1">
                      <AlertTriangle size={9} /> {inc.exposure_count}
                    </span>
                  ) : (
                    <span className="text-text-muted text-[10px]">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Tag assets with <code className="text-blue-light">model_vendor=&lt;vendor&gt;</code> for explicit mapping.
        Software-inference walks <code className="text-blue-light">cdm_software_installations</code>; banks should
        still tag explicitly to remove the "untagged AI assets" follow-up. Generated{' '}
        {data?.generated_at ? formatDate(data.generated_at) : '—'}.
      </p>
    </div>
  )
}


// ── Small components ─────────────────────────────────────────────────────────

function Tile({
  icon: Icon, label, value, tone, warn,
}: {
  icon: React.ElementType
  label: string
  value: number
  tone: string
  warn?: boolean
}) {
  return (
    <div className={clsx('card p-3', warn && 'ring-1 ring-amber/40')}>
      <div className="text-[10px] uppercase tracking-widest text-text-muted flex items-center gap-1">
        <Icon size={11} className="text-text-muted" /> {label}
      </div>
      <div className={clsx('text-2xl font-black font-mono mt-1', tone)}>{value.toLocaleString()}</div>
    </div>
  )
}
